import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FaEdit, FaTrash, FaBook, FaClipboardList, FaBarcode, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { fetchMentorClassrooms, updateClassroom, deleteClassroom } from "api";
import { SpiderwebEffect } from "components/card/WebEffect";
import { fireSwal } from "utils/swalConfig";
import ClassroomDetails from "views/admin/dashboards/mentorHub/pages/ClassroomDetails";
import InputField from "components/fields/InputField";
import DottedLoader from "components/loaders/DottedLoader";
import { useAuth } from "contexts/authContext";

// --- Child Components ---
const ClassroomCard = ({ classroom, onSelect, onEdit, onDelete, isSelected }) => (
    <div className={`group relative rounded-2xl transition-all duration-300 ${isSelected ? 'ring-4 ring-orange-500/50 dark:ring-brand-500/50' : 'shadow-lg hover:shadow-xl hover:-translate-y-1'}`}>
        <div onClick={onSelect} className={`relative p-6 bg-white dark:bg-navy-800 rounded-2xl cursor-pointer overflow-hidden`}>
            <SpiderwebEffect isSelected={isSelected} />
            <div className="relative z-10">
                <div className="flex justify-between items-start">
                    <div className="space-y-2">
                        <p className="font-bold text-xl text-navy-700 dark:text-white">{classroom.name}</p>
                        <p className="flex items-center gap-2 text-base text-gray-500 dark:text-gray-400"><FaBook /> {classroom.course}</p>
                    </div>
                    <div className="flex items-center gap-2 text-base font-mono bg-gray-100 dark:bg-navy-700 px-3 py-1.5 rounded-full text-navy-700 dark:text-white">
                        <FaBarcode /><span>{classroom.code}</span>
                    </div>
                </div>
                <div className="mt-8 flex justify-between items-end">
                    <div className="flex items-center text-lg text-gray-600 dark:text-gray-300 font-semibold">
                        <FaClipboardList className="mr-2" />
                        <span>Grade {classroom.grade}</span>
                    </div>
                    <div className="flex space-x-2">
                        <button onClick={(e) => { e.stopPropagation(); onEdit(classroom); }} className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-navy-700"><FaEdit size={20} className="text-brand-500"/></button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(classroom.id); }} className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-navy-700"><FaTrash size={20} className="text-red-500"/></button>
                    </div>
                </div>
            </div>
            <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-orange-500 dark:bg-gray-800 text-white text-xs font-semibold rounded-full opacity-0 transition-opacity duration-300 whitespace-nowrap z-20 ${!isSelected && 'group-hover:opacity-100'}`}>
                Click to view class details
            </div>
        </div>
    </div>
);

const Pagination = ({ totalPages, currentPage, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex justify-center items-center gap-2 mt-8">
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-navy-700"><FaChevronLeft className="text-gray-600 dark:text-gray-300" /></button>
            <span className="text-navy-700 dark:text-white font-semibold">Page {currentPage} of {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-navy-700"><FaChevronRight className="text-gray-600 dark:text-gray-300" /></button>
        </div>
    );
};

// --- Main Component ---
const ClassroomList = () => {
    const queryClient = useQueryClient();
    const { currentUser } = useAuth();
    const [selectedClassroomId, setSelectedClassroomId] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingClassroom, setEditingClassroom] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // Fetch data using useQuery
    const { data: classrooms = [], isLoading, isError, error } = useQuery({
        queryKey: ['mentorClassrooms'],
        queryFn: fetchMentorClassrooms,
    });

    // Listen for classroomCreated event
    useEffect(() => {
        const handleClassroomCreated = () => {
            queryClient.invalidateQueries({ queryKey: ['mentorClassrooms'] });
        };

        window.addEventListener('classroomCreated', handleClassroomCreated);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('classroomCreated', handleClassroomCreated);
        };
    }, [queryClient]);

    // Handle updates with useMutation
    const updateMutation = useMutation({
        mutationFn: updateClassroom,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mentorClassrooms'] });
            setIsEditModalOpen(false);
            fireSwal({ title: 'Updated!', text: 'Classroom details saved.', icon: 'success', timer: 1500, showConfirmButton: false });
        },
        onError: (err) => {
            fireSwal({ title: 'Error!', text: err.message || 'Failed to update classroom.', icon: 'error' });
        }
    });

    // Handle deletions with useMutation
    const deleteMutation = useMutation({
        mutationFn: deleteClassroom,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mentorClassrooms'] });
            if (selectedClassroomId) setSelectedClassroomId(null);
            fireSwal({ title: 'Deleted!', text: 'The classroom has been deleted.', icon: 'success', timer: 1500, showConfirmButton: false });
        },
        onError: (err) => {
            fireSwal({ title: 'Error!', text: err.message || 'Failed to delete classroom.', icon: 'error' });
        }
    });

    const handleEdit = (classroom) => {
        setEditingClassroom({ ...classroom });
        setIsEditModalOpen(true);
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        updateMutation.mutate(editingClassroom);
    };

    const handleDelete = async (classroomId) => {
        const result = await fireSwal({
            title: 'Are you sure?',
            text: "This action will permanently delete the classroom.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            actionType: 'delete'
        });
        if (result.isConfirmed) {
            deleteMutation.mutate(classroomId);
        }
    };

    // Filtering and Pagination Logic
    const filteredClassrooms = classrooms.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.course.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.grade.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const totalPages = Math.ceil(filteredClassrooms.length / itemsPerPage);
    const currentClassrooms = filteredClassrooms.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const selectedClassroom = classrooms.find(c => c.id === selectedClassroomId);

    if (isLoading) return <DottedLoader text="Loading classrooms..." />;
    if (isError) return <div className="text-center text-red-500">Error: {error.message}</div>;

    return (
        <>
            <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-2xl p-6 sm:p-8">
                {/* Search and Header */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-6">
                    <h2 className="text-3xl font-bold text-navy-700 dark:text-white">Your Classrooms <span className="text-xl font-medium text-gray-400">({filteredClassrooms.length})</span></h2>
                    <InputField placeholder="Search..." id="search" type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} extra="w-full md:w-80" />
                </div>
                {/* Classroom Grid */}
                {filteredClassrooms.length === 0 ? (
                    <div className="text-center py-20 border-2 border-dashed border-gray-200 dark:border-navy-700 rounded-lg">
                        <h3 className="mt-4 text-2xl font-semibold text-gray-700 dark:text-gray-200">No Classrooms Found</h3>
                        <p className="mt-2 text-lg text-gray-500 dark:text-gray-400">{searchTerm ? `No results for "${searchTerm}"` : "Create a new classroom to get started."}</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                            {currentClassrooms.map(classroom => (
                                <ClassroomCard key={classroom.id} classroom={classroom} isSelected={selectedClassroomId === classroom.id} onSelect={() => setSelectedClassroomId(classroom.id === selectedClassroomId ? null : classroom.id)} onEdit={handleEdit} onDelete={handleDelete} />
                            ))}
                        </div>
                        <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} />
                    </>
                )}
            </div>
            {/* Details Panel */}
            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${selectedClassroomId ? 'max-h-[5000px] opacity-100 mt-8' : 'max-h-0 opacity-0'}`}>
                {selectedClassroomId && (
                    <div className="relative bg-white dark:bg-navy-800 rounded-2xl shadow-2xl">
                        <div className="p-8">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-2xl font-bold text-navy-700 dark:text-white">{selectedClassroom ? `${selectedClassroom.name} Workspace` : 'Workspace'}</h3>
                                <button onClick={() => setSelectedClassroomId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-2 rounded-full"><IoClose size={28} /></button>
                            </div>
                            <ClassroomDetails id={selectedClassroomId} />
                        </div>
                    </div>
                )}
            </div>
            {/* Edit Modal */}
            {isEditModalOpen && editingClassroom && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                    <div className="bg-white dark:bg-navy-800 rounded-lg p-6 w-full max-w-md shadow-xl">
                        <h2 className="text-xl font-bold mb-6 text-navy-700 dark:text-white">Edit Classroom</h2>
                        <form onSubmit={handleUpdate}>
                            <div className="space-y-4">
                                <InputField label="Classroom Name" id="name" type="text" value={editingClassroom.name} onChange={(e) => setEditingClassroom({ ...editingClassroom, name: e.target.value })} />
                                <InputField label="Grade" id="grade" type="text" value={editingClassroom.grade} onChange={(e) => setEditingClassroom({ ...editingClassroom, grade: e.target.value })} />
                                <InputField label="Course" id="course" type="text" value={editingClassroom.course} onChange={(e) => setEditingClassroom({ ...editingClassroom, course: e.target.value })} />
                            </div>
                            <div className="flex justify-end space-x-4 mt-8">
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 rounded-lg border text-navy-700 dark:text-white border-gray-300 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700">Cancel</button>
                                <button type="submit" className="px-4 py-2 rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50" disabled={updateMutation.isLoading}>
                                    {updateMutation.isLoading ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClassroomList;